import {IPopupable} from "/@back@/commons/widgets/popupable.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {Tabs} from "/@back@/commons/widgets/tabs.js";
import {IWedEditorBoxSel} from "/@back@/edit/wed/features/boxSel.js";
import {IWedEditorCommonBar} from "/@back@/edit/wed/features/commonBar.js";
import {IWedletModel} from "/@back@/edit/wed/wedCore.js";
import {IWedEdBar, IWedEditor, IWedEditorMain, OWedEditorConfig, OWedEditorMainConfig, OWedManagerConfig, WedMgr} from "/@back@/edit/wed/wedEditor.js";
import {OWedEditorBoxConfig, WedEditorBox} from "/@back@/edit/wed/wedEditorBox.js";
import {findElementWedlet, IChildWedlet, isSkAnnotListener, IWedlet, markAsScrollCtn, WEDLET} from "/@back@/edit/wed/wedlets/wedlet.js";
import {Action, IActionToggle} from "/@lib@/commons/actions.js";
import {Area} from "/@lib@/commons/areas.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {IDocHolderSync} from "/@lib@/edit/docHolder.js";
import {Button, ButtonToggle, OButtonInit, OButtonToggleInit} from "/@back@/commons/widgets/buttons.js";
import {IWedEditorSearchBar} from "/@back@/edit/wed/features/searchBar.js";
import {IWedEditorDiffBar, OWedEditorDiffBarConfig} from "/@back@/edit/wed/features/diffBar.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {REG} from "/@lib@/commons/registry.js";
import {IWedletSingleElt} from "/@back@/edit/wed/wedlets/wedletSingleElt.js";
import {IJmlNode, IJmlSubSet, JML} from "/@lib@/commons/xml/jml.js";
import {XA} from "/@lib@/commons/xml/xAddr.js";
import {OLabelInit} from "/@back@/commons/basis.js";
import {JLastDatas, LASTDATAS} from "/@lib@/commons/lastDatas.js";
import {ISkAnnot} from "/@lib@/edit/schema/schemaAnnots.js";

/** Config de l'edditor dédié à la gestion des metas. */
export interface OWedEditorMetaConfig extends OWedEditorMainConfig {
	/**
	 * Affichage de la barre des métas :
	 * - none : pas de barre de metas disponible.
	 * - available : barre de métas dispo mais masquée à l'init, affichable à la demande.
	 * - shown : barre de métas dispo et affichée.
	 */
	metaBarMode?: 'none' | 'available' | 'shown'
}

/** Enrichissements de l'éditor principal pour gérer l'editor de metas. */
export interface IWedEditorMetaBar extends IWedEditorMain {
	metaBar?: MetaBar
	metaBarMode?: 'none' | 'available' | 'shown'

	showMetaBar?(show: boolean): MetaBar
}

export function AgMetaBarEditor(cls: any): any {
	const proto = cls.prototype as IWedEditorMetaBar;

	const superInit = proto.initialize;
	proto.initialize = function (this: IWedEditorMetaBar, init: OWedEditorMetaConfig) {
		superInit.call(this, init);
		if (metaBarLD === undefined) {
			metaBarLD = LASTDATAS.getLastDatas(document.body, "wedMetaBar") || null;
			LASTDATAS.buildLastDatasHooks.add((ld) => {
				if (metaBarLD) ld["wedMetaBar"] = metaBarLD;
			});
		}
		this.metaBarMode = init.metaBarMode || (metaBarLD ? 'shown' : 'available');
		if (this.metaBarMode === 'shown') this.showMetaBar(true);
		return this;
	};

	proto.showMetaBar = function (this: IWedEditorMetaBar & IWedEditorCommonBar, show: boolean): MetaBar {
		if (this.metaBarMode === 'none') return null;
		if (show) {
			if (!this.metaBar) {
				this.metaBar = new MetaBar().initMetaBar(this);
			} else this.metaBar.hidden = false;
			this.metaBarMode = 'shown';
			this.barLayout.showBar(this.metaBar);
			metaBarLD = {}
		} else {
			this.metaBarMode = 'available';
			metaBarLD = null;
			if (this.metaBar && DOM.setHidden(this.metaBar, true)) this.barLayout.onHideBar(this.metaBar);
		}
		return this.metaBar;
	};

	return cls;
}

class MetaBar extends HTMLElement implements CustomElement, IWedEdBar {
	mainEditor: IWedEditorMetaBar;
	titleTag: HTMLElement;

	/** Références des métas en cours d'édition. */
	metaDefs: IMetaDef[];
	currentMetaDef: IMetaDef;
	currentWedlet: IChildWedlet;

	gotoHolderBtn: Button;

	constructor() {
		super();
		this.id = "metaBar";
	}

	initMetaBar(mainEditor: IWedEditorMetaBar): this {
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		mainEditor.reg.installSkin("scroll/large", sr);
		mainEditor.reg.installSkin("wed-meta-bar", sr);
		this.titleTag = <div id="titleMeta">Propriétés</div>;
		this.gotoHolderBtn = <Button î={{disabled: true, icon: "/@skin@/edit/wed/metaBarHolderBtn.svg", title: "Aller au conteneur de ces propriétés"} satisfies OButtonInit} onclick={this.onShowMetaHolder}/> as Button;
		sr.append(
			<div id="titleHead">
				{this.titleTag}
				{this.gotoHolderBtn}
				<Button î={{icon: "/@skin@/commons/icons/close.svg", title: "Fermer les propriétés"} satisfies OButtonInit} onclick={this.onClose}/>
			</div>,
			markAsScrollCtn(<div id="scrollBox">
				<slot/>
			</div>)
		);

		//Connexion au mainEditor.
		this.mainEditor = mainEditor;
		const mainWedMgr = this.mainEditor.wedMgr;

		mainWedMgr.listeners.on("selection", (wedMgr: WedMgr, sel: Selection | null) => {
			if (!sel) return;
			if (this.mainEditor.metaBarMode !== "shown") return;
			const node = sel.type === "Range" ? sel.getRangeAt(0).commonAncestorContainer : sel.focusNode;
			if (!node) return;
			if (!DOMSH.isAncestor(wedMgr.wedEditor.mainContainer, node)) return; //Pas dans la zone d'édition principale.
			let wedlet = findElementWedlet(node)?.wedlet;
			if (wedlet?.wedMgr !== mainWedMgr) return;
			while (wedlet) {
				if (isWithMetaWedlet(wedlet) && wedlet.hasMetaDefs()) {
					this.editMeta(wedlet.getMetaDefs());
					return;
				}
				wedlet = wedlet.wedParent;
			}
			//Pas trouvé de metas à éditer.
			this.editMeta(null);
		});

		mainWedMgr.listeners.on("getFocus", (wedMgr: WedMgr, focused: Element) => {
			if (this.mainEditor.metaBarMode !== "shown") return;
			if (!DOMSH.isAncestor(wedMgr.wedEditor.mainContainer, focused)) return; //Pas dans la zone d'édition principale.
			let fromWedlet = findElementWedlet(focused)?.wedlet;
			let wedlet = fromWedlet;
			while (wedlet) {
				if (isWithMetaWedlet(wedlet) && wedlet.hasMetaDefs()) {
					this.editMeta(wedlet.getMetaDefs());
					return;
				}
				wedlet = wedlet.wedParent;
			}
			//Pas trouvé de metas à éditer.
			if (fromWedlet) {
				let currWedlet: IWedlet = (wedMgr.wedEditor as IWedEditorMetaBar).metaBar.metaDefs?.[0]?.from;
				while (currWedlet) {
					if (currWedlet === fromWedlet) return; //Ancêtre des meta en cours d'édition, on ne reset pas
					currWedlet = currWedlet.wedParent;
				}
			}
			this.editMeta(null);
		});

		mainWedMgr.listeners.on("hookSkAnnotAfter", (wedMgr: WedMgr, removed: ISkAnnot[], added: ISkAnnot[]) => {
			//Ajuste les annots des metas affichés sur le holder.
			if (!this.currentWedlet) return;
			const holder = this.currentMetaDef.from;
			if (!isSkAnnotListener(holder)) return;
			const xaMeta = this.currentWedlet.wedAnchor;
			if (removed) for (const an of removed) {
				if (XA.isAncOrEquals(xaMeta, an.start)) holder.onRemovedSkAnnot(an, an.anchor || an.start);
			}
			if (added) for (const an of added) {
				if (XA.isAncOrEquals(xaMeta, an.start)) holder.onAddedSkAnnot(an, an.anchor || an.start);
			}
		});

		const clear = this.clearAllMeta.bind(this);
		mainWedMgr.hookFct("clearEditor", clear);
		mainWedMgr.listeners.on("redrawAtEnd", clear);
		this.addEventListener("wed-editor-root-deleted", clear); //XXXX A vérifier ??
		return this;
	}

	isAlreadyEditing(metaDefs: IMetaDef | IMetaDef[]): boolean {
		if (!this.metaDefs) return false;
		if (Array.isArray(metaDefs)) {
			const l = metaDefs.length;
			return l === this.metaDefs.length && metaDefs[l - 1] === this.metaDefs[l - 1];
		}
		return this.metaDefs[0].from === (Array.isArray(metaDefs) ? metaDefs[0] : metaDefs).from;
	}

	editMeta(metaDefs: IMetaDef | IMetaDef[] | null) {
		if (metaDefs == null) {
			//reset
			if (this.metaDefs) this.clearAllMeta();
			return;
		}
		if (this.isAlreadyEditing(metaDefs)) return;
		this.clearCurrentMeta();
		//console.log("editMeta:::", metaDefs);
		if (Array.isArray(metaDefs)) {
			this.metaDefs = metaDefs;
			const last = metaDefs.length - 1;
			this.titleTag.textContent = "Propriétés :";
			for (let i = 0; i <= last; i++) {
				this.titleTag.appendChild(<ButtonToggle meta-idx={i} î={{
					label: metaDefs[i].label,
					toggleOn: i == last,
					uiContext: "custom"
				} satisfies OButtonToggleInit} onclick={this.onMultiMeta}/>)
			}
			this.buildMetaContent(metaDefs[last]);
		} else {
			this.metaDefs = [metaDefs];
			this.titleTag.textContent = `Propriétés : ${metaDefs.label}`;
			this.buildMetaContent(metaDefs);
		}
	}

	protected buildMetaContent(metaDef: IMetaDef) {
		if (this.currentMetaDef === metaDef) return;
		this.textContent = null;
		const wedletMeta = metaDef.model.createWedlet(metaDef.from.wedParent) as IChildWedlet & IWedletSingleElt;
		wedletMeta.insertElement(this, null);
		const wedMgr = this.mainEditor.wedMgr;
		const xa = metaDef.from.wedAnchor;
		const jml = wedMgr.getContent(xa);
		wedletMeta.bindWithNode(XA.last(xa) as number, JML.getJmlNode(jml, 0) as IJmlNode, JML.getJmlChildrenOrBody(jml, 0) as IJmlSubSet);
		this.currentMetaDef = metaDef;
		this.currentWedlet = wedletMeta;
		this.gotoHolderBtn.disabled = false;
		metaDef.from.editingMeta(metaDef, wedletMeta);
		wedMgr.dispatchAnnotsAfterRebuildWedlet(xa);
	}

	protected clearAllMeta() {
		if (!this.metaDefs) return;
		this.metaDefs = null;
		this.titleTag.textContent = "Propriétés";
		this.clearCurrentMeta();
	}

	protected clearCurrentMeta() {
		if (!this.currentMetaDef) return;
		//console.trace("clearMeta:::");
		this.currentMetaDef.from.editingMeta(this.currentMetaDef, null);
		this.currentMetaDef = null;
		this.currentWedlet = null;
		this.textContent = null;
		this.gotoHolderBtn.disabled = true;
	}

	protected onClose(this: Button) {
		const me = DOMSH.findHost<MetaBar>(this);
		me.clearAllMeta();
		me.mainEditor.showMetaBar(false);
	}

	protected onShowMetaHolder(this: Button) {
		const me = DOMSH.findHost<MetaBar>(this);
		const from = me.currentMetaDef?.from;
		if (from) WEDLET.highlightWedletFromLink(from.wedAnchor, me.mainEditor.wedMgr, "nearest");
	}

	protected onMultiMeta(this: ButtonToggle) {
		if (this.toggleOn) return;
		const me = DOMSH.findHost<MetaBar>(this);
		const metaIdx = parseInt(this.getAttribute("meta-idx"));
		if (metaIdx >= 0) {
			this.toggleOn = true;
			let other = this.previousElementSibling;
			while (other instanceof ButtonToggle) {
				other.toggleOn = false;
				other = other.previousElementSibling;
			}
			other = this.nextElementSibling;
			while (other instanceof ButtonToggle) {
				other.toggleOn = false;
				other = other.nextElementSibling;
			}
			me.buildMetaContent(me.metaDefs[metaIdx]);
		}
	}
}

REG.reg.registerSkin("wed-meta-bar", 1, /*language=CSS*/ `
	:host {
		flex: 1;
		display: flex;
		flex-direction: column;
		min-height: 0;
		min-width: 0;
	}

	#titleHead {
		display: flex;
		align-items: center;
		background-color: var(--alt1-bgcolor);
		border-bottom: 1px solid var(--border-color);
		padding: .1em .3em;
		color: var(--alt1-color);
		font-weight: bold;
	}

	#titleMeta {
		flex: 1;
		display: flex;
		align-items: center;
	}

	c-button-toggle {
		margin: 0 .5em;
		color: var(--tabl-select-color);
	}

	c-button-toggle[aria-pressed=true] {
		border-bottom: 2px solid var(--tabl-select-color);
		-webkit-text-stroke: 0.5px var(--tabl-select-color);
	}

	#scrollBox {
		flex: 1;
		overflow-y: auto;
		scrollbar-gutter: stable;
		min-height: 3em;
		scroll-behavior: smooth;
	}
`);

customElements.define("wed-meta-bar", MetaBar);

interface JMetaBarLD extends JLastDatas {
	//h: number
}

/** Mémoire de la présence de la metaBar, quelquesoit l'atelier courant. */
let metaBarLD: JMetaBarLD;

/** Activation de la panel/bar des metas*/
export class ShowMetaBarAction extends Action<IWedEditor & IWedEditorMetaBar> implements IActionToggle<IWedEditor & IWedEditorMetaBar> {

	constructor() {
		super("metaBar");
		this._label = "Propriétés";
	}

	isVisible(ctx: IWedEditor & IWedEditorMetaBar) {
		return ctx.metaBarMode && ctx.metaBarMode !== 'none';
	}

	isToggle(): this is IActionToggle<IWedEditor & IWedEditorMetaBar> {return true}

	getDatas(api: 'toggle', ctx: IWedEditor & IWedEditorMetaBar): boolean | null {
		return ctx.metaBar != null && !ctx.metaBar.hidden;
	}

	execute(ctx: IWedEditor & IWedEditorMetaBar, ev?: Event): any | 'noPreventDefault' {
		ctx.showMetaBar(!ctx.metaBar || ctx.metaBar.hidden);
	}
}

/** Wedlets */
export interface IWithMetaWedlet extends IWedlet {
	hasMetaDefs(): boolean

	getMetaDefs(): IMetaDef | IMetaDef[]

	editingMeta(metaDef: IMetaDef, wedlet: IChildWedlet | null): void
}

function isWithMetaWedlet(w: IWedlet | null): w is IWithMetaWedlet {return w && typeof (w as IWithMetaWedlet).hasMetaDefs === "function"}

export interface IMetaDef {
	label: string
	//TODO icon?: string
	//TODO desc?:string
	from: IWithMetaWedlet,
	model: IWedletModel
}

export namespace METAS {

	// /**
	//  * Affiche les metas dans un panelBar de l'editeur, ou à défaut en popupDialog.
	//  * Retourne une Promise si ouverture d'un dialogue, null si affichage dans un panel.
	//  * TODO pour ce mode avec la barre affichée en permanence, implémenter la synchro avec la sel courante à partir de txtSel
	//  */
	// export function showMeta(metaDefs: IMetaDef | IMetaDef[]): Promise<void> | null {
	// 	const firstDef = Array.isArray(metaDefs) ? metaDefs[0] : metaDefs;
	// 	const fromWedMgr = firstDef.from.wedMgr;
	// 	const fromEditor = fromWedMgr.wedEditor as IWedEditorMetaBar;
	// 	if (fromEditor.metaBarMode === "shown" && !Array.isArray(metaDefs) /* TODO metaBar en multi metas */) {
	// 		fromEditor.metaBar.editMeta(metaDefs);
	// 		return null;
	// 	}
	// 	//Ouverture en popup dialog...
	// 	return openMeta(metaDefs);
	// }

	/**
	 * Affiche les metas dans un popup dialog.
	 * Retourne une Promise à la fermeture du dialogue.
	 */
	export function openMeta(metaDefs: IMetaDef | IMetaDef[]): Promise<void> {
		const firstDef = Array.isArray(metaDefs) ? metaDefs[0] : metaDefs;
		const fromWedMgr = firstDef.from.wedMgr;
		const multiMetas = Array.isArray(metaDefs) && metaDefs.length > 0;
		let popup: IPopupable<void>;
		const popupLabel = (firstDef.from.wedMgr.wedEditor as IWedEditorMetaBar).metaBarMode !== "available"
			? {label: multiMetas ? "Propriétés" : `Propriétés : ${firstDef.label}`} as OLabelInit
			: () => <div style="flex:1; display:flex">
				<c-label ellipsis="">{multiMetas ? "Propriétés" : `Propriétés : ${firstDef.label}`}</c-label>
				<Button î={{title: "Afficher les propriétés dans l'éditeur principal", icon: "/@skin@/edit/wed/metaBarBtn.svg"} satisfies OButtonInit}
								onclick={function (ev: Event) {
									(firstDef.from.wedMgr.wedEditor as IWedEditorMetaBar).showMetaBar(true);
									popup.close();
								}}
				/>
			</div>;
		if (multiMetas) {
			const tabs = new Tabs().initialize({
				areas: metaDefs.map((m: IMetaDef) => new MetaEditorTab(m)),
				areasContext: null,
				skinOver: "webzone:panel"
			});
			tabs.style.margin = ".5em";
			tabs.style.flex = "1";
			popup = POPUP.showDialog(tabs, fromWedMgr.wedEditor.rootNode, {
				titleBar: {barLabel: popupLabel, closeButton: dialogMetasCloseBtn},
				resizer: {},
				initWidth: '80vw',
				initHeight: '60vh',
				closeOnCtrlEnter: true
			});
		} else {
			const editor = new MetaEditorTab(firstDef).buildBody();
			editor.reg.installSkin("webzone:panel", editor.shadowRoot);
			//Focus le 1er wed focusable
			editor.wedMgr.listeners.once("redrawAtEnd", (wedMgr: WedMgr) => {
				(wedMgr.wedEditor as IWedEditorBoxSel).boxSelMgr.focusFirst(wedMgr.rootWedlet);
			});
			popup = POPUP.showDialog(editor, fromWedMgr.wedEditor.rootNode, {
				titleBar: {barLabel: popupLabel, closeButton: dialogMetasCloseBtn},
				resizer: {},
				initWidth: '80vw',
				initHeight: '60vh',
				closeOnCtrlEnter: true
			});
		}
		popup.addEventListener('wed-editor-root-deleted', () => {
			POPUP.showNotifInfo("L'élément portant ces propriétés a été supprimé.", fromWedMgr.wedEditor.rootNode);
			popup.close();
		});
		return popup.onNextClose();
	}

	/** Edite des métas dans une house locale avant insertion dans l'item lors de la création d'une balise. */
	export async function openMetaNew(wedMgr: WedMgr, docHolder: IDocHolderSync, metaModel: IWedletModel, title: string, parent: IWedlet): Promise<void> {
		const wedMgrConfig = Object.create(wedMgr.config) as OWedManagerConfig;
		wedMgrConfig.readOnlyCauses = Array.from(wedMgr.readOnlyCauses);
		wedMgrConfig.xaRoot = [0];
		wedMgrConfig.rootModel = metaModel;
		if (parent && !WEDLET.isWritableWedlet(parent)) wedMgrConfig.readOnlyCauses = ["wedletOwner"];
		const editorConfig = Object.create(wedMgr.wedEditor.config) as OWedEditorConfig & OWedEditorDiffBarConfig & OWedEditorMetaConfig;
		editorConfig.diffBarMode = 'none'; //pas de diff pour metas en création
		editorConfig.metaBarMode = 'none';
		(editorConfig as any).hidel10nDrvBar = true;// pas de diff DRV OWedEditorL10nDrvConfig
		const newEditor = new WedEditorBox().initialize(editorConfig) as WedEditorBox;
		newEditor.reg.installSkin("webzone:panel", newEditor.shadowRoot);
		newEditor.wedModel = wedMgr.wedModel;
		newEditor.initFromDocHolder(docHolder, wedMgrConfig);
		//Focus le 1er wed focusable
		newEditor.wedMgr.listeners.once("redrawAtEnd", (wedMgr: WedMgr) => {
			(wedMgr.wedEditor as IWedEditorBoxSel).boxSelMgr.focusFirst(wedMgr.rootWedlet);
		});
		await POPUP.showDialog(newEditor, wedMgr.wedEditor.rootNode, {
			titleBar: {barLabel: {label: title}, closeButton: dialogMetasCloseBtn},
			resizer: {},
			initWidth: '80vw',
			initHeight: '60vh',
			closeOnCtrlEnter: true
		}).onNextClose();
	}

	const dialogMetasCloseBtn: OButtonInit = {uiContext: 'dialog', label: "Fermer", title: "Fermer l'édition de ces propriétés (vos modifications sont préservées)"};
}

/** Area construisant l'editor d'un meta. */
class MetaEditorTab extends Area<null, WedEditorBox> {

	constructor(public metaDef: IMetaDef) {
		super();
	}

	getLabel() {return this.metaDef.label}

	buildBody(): WedEditorBox {
		const fromWedMgr = this.metaDef.from.wedMgr;
		const editorConfig = Object.create(fromWedMgr.wedEditor.config) as OWedEditorBoxConfig & OWedEditorMetaConfig;
		editorConfig.disableVirtuals = fromWedMgr.reg.getPref("wed.metaBar.disableVirtuals", false);
		editorConfig.metaBarMode = "none";
		(editorConfig as any).hidel10nDrvBar = true;// pas de diff DRV OWedEditorL10nDrvConfig car impossible de retrouver de façon certaine le tag méta correspondant dans la source
		const editor = new WedEditorBox().initialize(editorConfig) as WedEditorBox & IWedEditorSearchBar & IWedEditorDiffBar;
		editor.wedModel = fromWedMgr.wedModel;
		const newDocHolder = fromWedMgr.docHolderAsync.cloneDocHolder();
		const wedMgrConfig = Object.create(fromWedMgr.config) as OWedManagerConfig;
		wedMgrConfig.readOnlyCauses = Array.from(fromWedMgr.readOnlyCauses);
		wedMgrConfig.xaRoot = this.metaDef.from.wedAnchor;
		wedMgrConfig.rootModel = this.metaDef.model;
		if (!WEDLET.isWritableWedlet(this.metaDef.from)) wedMgrConfig.readOnlyCauses = ["wedletOwner"];
		wedMgrConfig.undoRedoFilter = newDocHolder.isMsgFromUs.bind(newDocHolder);
		editor.initFromDocHolder(newDocHolder, wedMgrConfig);
		//transfert searchBar.
		const searchP = (fromWedMgr.wedEditor as IWedEditorSearchBar).searchBar?.getSearchParams();
		if (searchP) editor.searchBar?.setSearch(searchP);
		//transfert diffBar
		const diffP = (fromWedMgr.wedEditor as IWedEditorDiffBar).diffBar?.getDiffParams();
		if (diffP) editor.getDiffBar().then((bar) => {bar.setDiffParams(diffP)});
		editor.wedMgr.listeners.on("hidden", (wedMgr, closed) => {
			if (closed) wedMgr.killEditor();
		});
		return editor;
	}
}
